'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';

import { Menu, X } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { cn } from '@kit/ui/utils';

import { SelenosWordmark } from './arc-mark';
import { OpenChatButton } from './open-chat-button';

const LINKS = [
  { href: '#how', labelKey: 'navHow' },
  { href: '#proof', labelKey: 'navProof' },
  { href: '#pricing', labelKey: 'navPricing' },
  { href: '#faq', labelKey: 'navFaq' },
] as const;

/**
 * Top nav: transparent over the hero, off-white with a hairline rule once
 * scrolled. Wordmark left, anchor links centre-right (desktop), one teal CTA
 * right that opens the chat (the primary action). Below `md`, the anchor links
 * collapse into a disclosure menu (hamburger) so phone visitors keep section
 * navigation.
 */
export function SelenosNav() {
  const t = useTranslations('selenos');
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu on Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const solidBar = scrolled || menuOpen;

  return (
    <header
      className={cn(
        'fixed inset-x-0 top-0 z-50 transition-colors duration-300',
        solidBar
          ? 'border-b border-[var(--sel-line)] bg-[var(--sel-bg)]/85 backdrop-blur-md'
          : 'border-b border-transparent bg-transparent',
      )}
    >
      <nav className="mx-auto flex h-20 max-w-[1200px] items-center justify-between px-4 sm:px-6">
        <Link href="/" aria-label={t('navHomeAria')} className="text-base">
          <SelenosWordmark />
        </Link>

        <div className="hidden items-center gap-8 md:flex">
          {LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm text-[var(--sel-muted)] transition-colors hover:text-[var(--sel-ink)]"
            >
              {t(link.labelKey)}
            </a>
          ))}
        </div>

        {/* Desktop CTA */}
        <OpenChatButton className="hidden h-10 items-center rounded-full bg-[var(--sel-accent)] px-5 text-sm font-medium text-[var(--sel-accent-ink)] transition-colors hover:bg-[var(--sel-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--sel-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sel-bg)] focus-visible:outline-none md:inline-flex">
          {t('ctaPrimary')}
        </OpenChatButton>

        {/* Mobile hamburger (the CTA lives inside the panel below md) */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={t('navMenuToggle')}
          aria-expanded={menuOpen}
          aria-controls="selenos-mobile-menu"
          className="-mr-2 inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--sel-ink)] transition-colors hover:bg-[var(--sel-elev)] focus-visible:ring-2 focus-visible:ring-[var(--sel-accent)] focus-visible:outline-none md:hidden"
        >
          {menuOpen ? (
            <X aria-hidden="true" className="h-5 w-5" />
          ) : (
            <Menu aria-hidden="true" className="h-5 w-5" />
          )}
        </button>
      </nav>

      {/* Mobile disclosure panel: section links + the primary CTA. */}
      <div
        id="selenos-mobile-menu"
        inert={!menuOpen ? true : undefined}
        className={cn(
          'overflow-hidden border-t border-[var(--sel-line)] bg-[var(--sel-bg)] transition-[max-height,opacity] duration-300 ease-out motion-reduce:transition-none md:hidden',
          menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="mx-auto flex max-w-[1200px] flex-col gap-1 px-4 py-3 sm:px-6">
          {LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="rounded-lg px-2 py-3.5 text-base text-[var(--sel-ink)] transition-colors hover:bg-[var(--sel-elev)] focus-visible:ring-2 focus-visible:ring-[var(--sel-accent)] focus-visible:outline-none"
            >
              {t(link.labelKey)}
            </a>
          ))}
          <OpenChatButton
            onActivate={() => setMenuOpen(false)}
            className="mt-2 inline-flex h-12 items-center justify-center rounded-full bg-[var(--sel-accent)] px-6 text-base font-medium text-[var(--sel-accent-ink)] transition-colors hover:bg-[var(--sel-accent-strong)] focus-visible:ring-2 focus-visible:ring-[var(--sel-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--sel-bg)] focus-visible:outline-none"
          >
            {t('ctaPrimary')}
          </OpenChatButton>
        </div>
      </div>
    </header>
  );
}
